import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileUp, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { usePreviewStatement, useReconcileStatement } from '@/hooks/useStatements';
import { useOFXReconciliation } from './ofx/useOFXReconciliation';
import { ReconciliationBoard } from './ofx/ReconciliationBoard';
import type { Category } from '@/lib/api';

export interface OFXUploadProps {
  categories: Category[];
}

export const OFXUpload: React.FC<OFXUploadProps> = ({ categories }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const { toast } = useToast();
  const previewStatement = usePreviewStatement();
  const reconcileStatement = useReconcileStatement();
  const reconciliation = useOFXReconciliation();

  const processFile = (file: File) => {
    previewStatement.mutate(file, {
      onSuccess: (parsed) => {
        if (parsed.length === 0) {
          toast({
            title: 'Nada a importar',
            description: 'Este arquivo OFX não contém transações.',
          });
          return;
        }
        reconciliation.initializeFromPreview(file, parsed);
      },
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.ofx')) {
      toast({
        title: 'Formato inválido',
        description: 'Selecione um arquivo .ofx',
        variant: 'destructive',
      });
      return;
    }
    processFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const resetUpload = () => {
    reconciliation.reset();
    previewStatement.reset();
    reconcileStatement.reset();
  };

  const handleSubmit = () => {
    const payload = reconciliation.submissionPayload;
    if (!reconciliation.canSubmit) return;
    reconcileStatement.mutate(payload, {
      onSuccess: (result) => {
        const parts = [];
        if (result.created > 0) {
          parts.push(`${result.created} ${result.created === 1 ? 'registrada' : 'registradas'}`);
        }
        if (result.linked > 0) {
          parts.push(`${result.linked} ${result.linked === 1 ? 'vinculada' : 'vinculadas'}`);
        }
        toast({
          title: 'Importação concluída',
          description: parts.join(' · ') || 'Sem alterações.',
        });
        navigate('/transactions');
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Tente novamente em instantes.';
        toast({
          title: 'Não foi possível importar',
          description: message,
          variant: 'destructive',
        });
      },
    });
  };

  // Loading while parsing
  if (previewStatement.isPending) {
    return (
      <div className="bg-card border border-border rounded-xl p-16 flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
        <p className="text-sm text-muted-foreground">Analisando arquivo…</p>
      </div>
    );
  }

  // Reconciliation board after a successful preview
  if (reconciliation.pairs.length > 0) {
    return (
      <ReconciliationBoard
        reconciliation={reconciliation}
        categories={categories}
        onCancel={resetUpload}
        onSubmit={handleSubmit}
        isSubmitting={reconcileStatement.isPending}
      />
    );
  }

  // Drop zone (initial state or after reset)
  return (
    <div
      onClick={() => fileInputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
      className={cn(
        'bg-card border-2 border-dashed rounded-xl p-16 flex flex-col items-center justify-center gap-4 cursor-pointer transition-all select-none',
        isDragOver
          ? 'border-primary/60 bg-primary/5'
          : 'border-border hover:border-primary/40 hover:bg-muted/20',
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".ofx"
        className="hidden"
        onChange={handleFileInput}
      />
      <div
        className={cn(
          'w-14 h-14 rounded-full flex items-center justify-center transition-colors',
          isDragOver ? 'bg-primary/15' : 'bg-muted',
        )}
      >
        <FileUp
          className={cn(
            'h-6 w-6 transition-colors',
            isDragOver ? 'text-primary' : 'text-muted-foreground',
          )}
        />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-foreground mb-1">
          {isDragOver ? 'Solte o arquivo aqui' : 'Arraste e solte seu arquivo .ofx'}
        </p>
        <p className="text-xs text-muted-foreground">ou clique para selecionar</p>
      </div>
    </div>
  );
};
